// Level-up move-learning logic.
//
// Species/move data in /src/data does not yet carry explicit learnsets. This
// module owns the level -> move schedule via a built-in table keyed by the
// lowercase species name (matching the speciesId convention used elsewhere
// in the codebase, e.g. encounter tables in src/data/maps).
//
// moveId values are the stringified numeric ids from MOVES_DATA (e.g. "1").
// This matches the pendingMoveId pattern in src/stores/game-store.ts.

import { MOVES_DATA, type MoveData } from '@/data/moves';
import { SPECIES_DEX, type SpeciesData } from '@/data/species';

export type LearnableMove = {
  speciesId: string;
  level: number;
  moveId: string;
};

export type MoveLearnDecision =
  | { kind: 'auto'; moveId: string }
  | { kind: 'prompt'; moveId: string; replace: 'choose-or-skip' };

export type MoveDisplay = {
  id: string;
  name: string;
  type: string;
  power: number;
  accuracy: number;
  pp: number;
  description?: string;
};

const MAX_MOVES = 4;

// --- Indexes -----------------------------------------------------------------

const MOVES_BY_ID: ReadonlyMap<string, MoveData> = new Map(
  MOVES_DATA.map((m) => [String(m.id), m]),
);

const SPECIES_BY_ID: ReadonlyMap<string, SpeciesData> = new Map(
  SPECIES_DEX.map((s) => [s.name.toLowerCase(), s]),
);

// --- Default-by-type fallback -----------------------------------------------
//
// Used when a species has no explicit learnset entry. Keys are MoveData.type
// strings (sales, tech, finance, creative, ops, normal).

const DEFAULT_LEARNSET_BY_TYPE: Readonly<Record<string, ReadonlyArray<[number, string]>>> = {
  sales:    [[1, '11'], [1, '12'], [7, '1'],  [13, '2'],  [19, '13']],
  tech:     [[1, '11'], [1, '12'], [7, '3'],  [13, '14'], [19, '4'],  [25, '20']],
  finance:  [[1, '11'], [1, '12'], [7, '6'],  [13, '15'], [19, '18'], [25, '5']],
  creative: [[1, '11'], [1, '12'], [7, '7'],  [13, '8'],  [19, '16'], [25, '19']],
  ops:      [[1, '11'], [1, '12'], [7, '17'], [13, '9'],  [19, '10']],
  normal:   [[1, '11'], [1, '12']],
};

// Explicit per-species learnsets. Species not listed fall back to type1's
// default schedule. Keys are lowercased species names.
const SPECIES_LEARNSETS: Readonly<Record<string, ReadonlyArray<[number, string]>>> = {
  cratus: [
    [1, '11'], [1, '12'],
    [7, '1'], [13, '2'], [19, '13'],
    [22, '6'], [28, '15'], [34, '18'], [40, '5'],
  ],
  huskii: [
    [1, '11'], [1, '12'],
    [7, '3'], [13, '14'], [19, '4'], [25, '20'],
    [22, '7'], [28, '8'], [34, '16'], [40, '19'],
  ],
};

// --- Internal helpers --------------------------------------------------------

function getLearnsetFor(speciesId: string): ReadonlyArray<[number, string]> {
  const key = speciesId.toLowerCase();

  const explicit = SPECIES_LEARNSETS[key];
  if (explicit) return explicit;

  const species = SPECIES_BY_ID.get(key);
  if (!species) return [];

  const fallback = DEFAULT_LEARNSET_BY_TYPE[species.type1];
  return fallback ?? [];
}

// Resolve a loose species identifier (sprite key "surggie_3"/"char_surggie_3",
// numeric dex id "3", or species name) to the species name keying the
// learnsets above. Mirrors battle-participant's resolveSpecies — implemented
// locally because battle-participant imports this module, and importing it
// back would create a cycle.
function resolveToName(speciesId: string): string {
  const raw = speciesId.trim();

  const numeric = Number.parseInt(raw, 10);
  if (Number.isFinite(numeric) && String(numeric) === raw) {
    const byId = SPECIES_DEX.find((s) => s.id === numeric);
    if (byId) return byId.name;
  }

  const spriteKey = raw.replace(/^char_/, '');
  const bySprite = SPECIES_DEX.find((s) => s.sprite === spriteKey);
  if (bySprite) return bySprite.name;

  // Already a name (or unknown — getLearnsetFor returns [] for unknowns).
  return raw;
}

// --- Public API --------------------------------------------------------------

/**
 * Moves learned at EXACTLY `level`. Unlike getMovesLearnedAtLevel (which is
 * keyed by species name), this accepts the party model's speciesId (a sprite
 * key like "surggie_3") as well as numeric dex ids and names.
 */
export function getNewMovesAtLevel(speciesId: string, level: number): string[] {
  return getMovesLearnedAtLevel(resolveToName(speciesId), level);
}

export function getMovesLearnedAtLevel(speciesId: string, level: number): string[] {
  const learnset = getLearnsetFor(speciesId);
  const learned: string[] = [];
  for (const [lvl, moveId] of learnset) {
    if (lvl === level) learned.push(moveId);
  }
  return learned;
}

export function decideMoveLearn(currentMoves: string[], newMoveId: string): MoveLearnDecision {
  if (currentMoves.length < MAX_MOVES) {
    return { kind: 'auto', moveId: newMoveId };
  }
  return { kind: 'prompt', moveId: newMoveId, replace: 'choose-or-skip' };
}

export function applyMoveLearnChoice(
  currentMoves: string[],
  newMoveId: string,
  choice: { kind: 'replace'; replaceIndex: number } | { kind: 'skip' },
): string[] {
  if (choice.kind === 'skip') {
    return currentMoves.slice(0, MAX_MOVES);
  }

  // Auto-learn path: if there's still a free slot, append regardless of
  // replaceIndex so callers don't have to special-case it.
  if (currentMoves.length < MAX_MOVES) {
    return [...currentMoves, newMoveId];
  }

  const { replaceIndex } = choice;
  if (
    !Number.isInteger(replaceIndex) ||
    replaceIndex < 0 ||
    replaceIndex >= currentMoves.length
  ) {
    // Out-of-range replace is treated as a skip.
    return currentMoves.slice(0, MAX_MOVES);
  }

  const next = currentMoves.slice();
  next[replaceIndex] = newMoveId;
  return next;
}

export function getMoveDisplay(moveId: string): MoveDisplay | null {
  const move = MOVES_BY_ID.get(moveId);
  if (!move) return null;
  return {
    id: String(move.id),
    name: move.name,
    type: move.type,
    power: move.power,
    accuracy: move.accuracy,
    pp: move.pp,
    description: move.description,
  };
}
